import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession
} from "amazon-cognito-identity-js";
import { config } from "./config";
import type { Role } from "./types";

const userPool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.clientId
});

export interface AuthUser {
  email: string;
  sub: string;
  roles: Role[];
  idToken: string;
}

export interface NewPasswordChallenge {
  type: "newPasswordRequired";
  complete: (newPassword: string) => Promise<AuthUser>;
}

export type SignInResult = AuthUser | NewPasswordChallenge;

function sessionToUser(session: CognitoUserSession): AuthUser {
  const payload = session.getIdToken().decodePayload() as {
    email?: string;
    sub: string;
    "cognito:groups"?: Role[];
  };

  return {
    email: payload.email ?? "",
    sub: payload.sub,
    roles: payload["cognito:groups"] ?? [],
    idToken: session.getIdToken().getJwtToken()
  };
}

export function getCurrentUser(): Promise<AuthUser | null> {
  const current = userPool.getCurrentUser();
  if (!current) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    current.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session?.isValid()) {
        resolve(null);
        return;
      }

      resolve(sessionToUser(session));
    });
  });
}

/**
 * Returns a currently-valid ID token, transparently refreshing it with the
 * stored refresh token when the cached ID/access token has expired. Cognito ID
 * tokens live ~1 hour, so callers must fetch a fresh token per request instead
 * of reusing the one captured at sign-in time. Resolves null when there is no
 * usable session (refresh token expired or user signed out).
 */
export function getIdToken(): Promise<string | null> {
  const current = userPool.getCurrentUser();
  if (!current) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    current.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session?.isValid()) {
        resolve(null);
        return;
      }

      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export function signIn(email: string, password: string): Promise<SignInResult> {
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool
  });

  const authenticationDetails = new AuthenticationDetails({
    Username: email,
    Password: password
  });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => resolve(sessionToUser(session)),
      onFailure: reject,
      newPasswordRequired: () => {
        resolve({
          type: "newPasswordRequired",
          complete: (newPassword: string) =>
            new Promise((completeResolve, completeReject) => {
              cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
                onSuccess: (session) => completeResolve(sessionToUser(session)),
                onFailure: completeReject
              });
            })
        });
      }
    });
  });
}

export function signOut(): void {
  userPool.getCurrentUser()?.signOut();
}

/**
 * Starts the "forgot password" flow: Cognito emails a verification code to the
 * account's registered address (via the user pool's configured email — Cognito
 * default or SES). Resolves once the code has been sent so the UI can prompt for
 * it. Pair with {@link confirmPasswordReset} to set the new password.
 */
export function requestPasswordReset(email: string): Promise<void> {
  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      // Fired once the code is dispatched; either callback may be used depending
      // on the SDK path, so both resolve.
      inputVerificationCode: () => resolve(),
      onSuccess: () => resolve(),
      onFailure: reject
    });
  });
}

/** Completes the reset: verifies the emailed code and sets the new password. */
export function confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: reject
    });
  });
}
